import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, Tag, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { StarRating } from './StarRating';

// Template style configurations matching KuponTemplateSelector
const TEMPLATE_STYLES = {
  'classic': {
    card: 'bg-card',
    image: 'bg-gradient-to-br from-local-sage to-local-sage/60',
    badge: 'bg-reward-gold text-white',
    price: 'text-local-sage',
    button: 'bg-local-sage hover:bg-local-sage/90'
  },
  'gradient-sage': {
    card: 'bg-gradient-to-br from-emerald-600 to-emerald-800',
    image: 'bg-emerald-700/50',
    badge: 'bg-white text-emerald-700',
    price: 'text-white',
    button: 'bg-white text-emerald-700 hover:bg-white/90',
    text: 'text-white',
    muted: 'text-white/70'
  },
  'dark-luxury': {
    card: 'bg-gray-900',
    image: 'bg-gray-800',
    badge: 'bg-yellow-500 text-gray-900',
    price: 'text-yellow-500',
    button: 'bg-yellow-500 text-gray-900 hover:bg-yellow-400',
    text: 'text-white',
    muted: 'text-gray-400',
    border: 'border-yellow-500/30'
  },
  'sunset': {
    card: 'bg-gradient-to-br from-red-400 to-orange-500',
    image: 'bg-orange-600/50',
    badge: 'bg-white text-orange-600',
    price: 'text-white',
    button: 'bg-white text-orange-600 hover:bg-white/90',
    text: 'text-white',
    muted: 'text-white/80'
  },
  'ocean': {
    card: 'bg-gradient-to-br from-blue-600 to-cyan-500',
    image: 'bg-blue-700/50',
    badge: 'bg-white text-blue-600',
    price: 'text-white',
    button: 'bg-white text-blue-600 hover:bg-white/90',
    text: 'text-white',
    muted: 'text-white/80'
  },
  'minimal-mono': {
    card: 'bg-gray-100 dark:bg-gray-800',
    image: 'bg-gray-200 dark:bg-gray-700',
    badge: 'bg-black text-white dark:bg-white dark:text-black',
    price: 'text-black dark:text-white',
    button: 'bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200',
    border: 'border-black/20 dark:border-white/20'
  },
  'neon-purple': {
    card: 'bg-gradient-to-br from-violet-600 to-purple-500',
    image: 'bg-purple-700/50',
    badge: 'bg-white text-purple-600',
    price: 'text-white',
    button: 'bg-white text-purple-600 hover:bg-white/90',
    text: 'text-white',
    muted: 'text-white/80'
  },
  'gold-premium': {
    card: 'bg-gradient-to-br from-yellow-500 to-yellow-200',
    image: 'bg-yellow-600/50',
    badge: 'bg-gray-900 text-yellow-400',
    price: 'text-gray-900',
    button: 'bg-gray-900 text-yellow-400 hover:bg-gray-800',
    text: 'text-gray-900',
    muted: 'text-gray-700'
  }
};

export const KuponCard = ({ kupon }) => {
  const discount = Math.round(((kupon.originalPrice - kupon.discountedPrice) / kupon.originalPrice) * 100);
  
  const expiresAt = new Date(kupon.expiresAt);
  
  // Countdown state
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0, expired: false });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = expiresAt - now;
      
      if (diff <= 0) {
        return { hours: 0, minutes: 0, seconds: 0, expired: true };
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return { hours, minutes, seconds, expired: false };
    };
    
    // Initial calculation
    setTimeLeft(calculateTimeLeft());
    
    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [kupon.expiresAt]);
  
  // Format countdown display
  const formatCountdown = () => {
    if (timeLeft.expired) {
      return 'Expirado';
    }
    
    const pad = (n) => n.toString().padStart(2, '0');
    
    if (timeLeft.hours > 0) {
      return `${pad(timeLeft.hours)}:${pad(timeLeft.minutes)}:${pad(timeLeft.seconds)}`;
    }
    return `${pad(timeLeft.minutes)}:${pad(timeLeft.seconds)}`;
  };
  
  // Get template styles (default to classic)
  const templateId = kupon.templateId || 'classic';
  const styles = TEMPLATE_STYLES[templateId] || TEMPLATE_STYLES['classic'];
  
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -4 }}
      className={`${styles.card} rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-all border ${styles.border || 'border-border/50'}`}
      data-testid="kupon-card"
    >
      {/* Image */}
      <div className={`relative h-48 ${styles.image} overflow-hidden`}>
        {kupon.imageUrl ? (
          <img 
            src={kupon.imageUrl} 
            alt={kupon.title} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Tag className={`w-16 h-16 ${styles.text ? styles.text + '/50' : 'text-white/50'}`} />
          </div>
        )}
        
        {/* Discount Badge */}
        <div className={`absolute top-4 right-4 ${styles.badge} px-4 py-2 rounded-full font-bold text-sm`}>
          -{discount}%
        </div>
        
        {/* Distance Badge */}
        {kupon.distance !== undefined && (
          <div className="absolute top-4 left-4 bg-white/90 dark:bg-black/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {kupon.distance} km
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-6">
        {/* Business Name */}
        <div className={`text-xs ${styles.muted || 'text-muted-foreground'} mb-2 font-medium uppercase tracking-wide`}>
          {kupon.businessName}
        </div>
        
        {/* Title */}
        <h3 className={`font-manrope font-semibold text-lg mb-2 line-clamp-2 ${styles.text || ''}`}>
          {kupon.title}
        </h3>
        
        {/* Rating */}
        {kupon.averageRating > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <StarRating rating={kupon.averageRating} size="sm" />
            <span className={`text-xs ${styles.muted || 'text-muted-foreground'}`}>
              ({kupon.totalReviews} {kupon.totalReviews === 1 ? 'review' : 'reviews'})
            </span>
          </div>
        )}
        
        {/* Description */}
        <p className={`text-sm ${styles.muted || 'text-muted-foreground'} mb-4 line-clamp-2`}>
          {kupon.description}
        </p>
        
        {/* Price */}
        <div className="flex items-end gap-2 mb-4">
          <span className={`text-3xl font-bold ${styles.price}`}>
            {kupon.discountedPrice.toFixed(2)}€
          </span>
          <span className={`text-sm ${styles.muted || 'text-muted-foreground'} line-through mb-1`}>
            {kupon.originalPrice.toFixed(2)}€
          </span>
        </div>
        
        {/* Meta Info */}
        <div className={`flex items-center justify-between text-xs ${styles.muted || 'text-muted-foreground'} mb-4`}>
          <div className={`flex items-center gap-1.5 ${timeLeft.expired ? 'text-red-500' : timeLeft.hours < 2 ? 'text-amber-500' : ''}`}>
            <Clock className="w-4 h-4" />
            <span className="font-mono font-semibold">{formatCountdown()}</span>
          </div>
          <div className="flex items-center gap-1">
            <Tag className="w-4 h-4" />
            <span>{kupon.category}</span>
          </div>
        </div>

        {/* No Refunds Notice */}
        <p className={`text-[10px] ${styles.muted ? styles.muted + '/70' : 'text-muted-foreground/70'} text-center mb-3`}>
          Compra final. No admite devoluciones.
        </p>
        
        {/* Action */}
        <Link to={`/kupon/${kupon.id}`}>
          <Button 
            className={`w-full ${styles.button} rounded-full`}
            data-testid="view-kupon-button"
          >
            Ver Kupon
          </Button>
        </Link>
      </div>
      
      {/* Ticket Perforation Effect */}
      <div className="relative h-6 bg-background">
        <div className="absolute inset-x-0 -top-3 flex justify-center">
          <div className="flex gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="w-2 h-2 rounded-full bg-background" />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};